import GroupsIcon from '@mui/icons-material/Groups';
import MenuIcon from '@mui/icons-material/Menu'; // Ajout de l'importation
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HomeIcon from '@mui/icons-material/Home';
import { FormControlLabel, Skeleton, Switch } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MainLogo from 'assets/main-logo.png';
import UseAuthContext from 'context/AuthContext';
import UseLanguageContext from 'context/LanguageContext';
import PackageProContext from 'context/PackageProContext';
import moment from 'moment';
import { useContext, useEffect, useState, startTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link, matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import { getMaintenanceStatus, toggleMaintenanceMode } from 'services/admin-users';
import { GetUserOrganization, getVersion } from 'services/auth-service';
import { getFeatures } from 'services/revisions';
import { USER_ROLES } from 'utils/constants';
import { calcTotalAccountPoints, getUnlimitedPackageName } from 'utils/utils';
import ButtonCustom from './ButtonCustom';
import ButtonIcon from './ButtonIcon';
import GenericDialog from './GenericDialog';
import HeaderMenu from './HeaderMenu';
import LanguageSwitcher from './LanguageSwitcher';

const getActiveUnlimitedPackageId = (organization: any): string | null => {
  if (!organization?.transactions) return null;

  const now = new Date();
  const transactions = Object.values(organization.transactions || {});

  const activeUnlimited: any = transactions.find((t: any) =>
    t.status === 'success' &&
    t.unlimited === true &&
    t.type === 'CREDIT' &&
    new Date(t.expiry_date) > now
  );

  return activeUnlimited ? activeUnlimited.plan_id : null;
};

const getAccumulatedExpiryDate = (organization: any): Date | null => {
  if (!organization?.transactions) return null;

  const now = new Date();
  const transactions = Object.values(organization.transactions || {}) as any[];

  // Filter only ACTIVE packages (status === 'success' excludes cancelled packages)
  // This ensures that if an older package is cancelled, it won't be considered
  const activeUnlimitedPackages = transactions.filter((t: any) =>
    t.status === 'success' &&  // Only non-cancelled packages
    t.unlimited === true &&
    t.type === 'CREDIT' &&
    t.expiry_date &&
    new Date(t.expiry_date) > now  // Only non-expired packages
  );

  if (activeUnlimitedPackages.length === 0) return null;

  // Find the package with the most recent EXPIRY DATE (not creation date)
  // This is the accumulated expiry date from all active packages
  const latestPackage: any = activeUnlimitedPackages.reduce((latest: any, current: any) => {
    const latestDate = new Date(latest.expiry_date);
    const currentDate = new Date(current.expiry_date);
    return currentDate > latestDate ? current : latest;
  });

  return new Date(latestPackage.expiry_date);
};

const Header = () => {
  const { isAuthenticated, organizations, user, logout, role, adminLogout, organizationId } = useContext(UseAuthContext);
  const { language } = useContext(UseLanguageContext);
  const { hasPackagePro, verificationSaved, setHasPackagePro } = useContext(PackageProContext);
  const [accountPoints, setAccountPoints] = useState(0);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [hideBusinessLogo, setHideBusinessLogo] = useState(false);
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentLocation = useLocation();
  
  // Check Package Pro feature flag
  const { data: features } = useQuery('features', getFeatures, {
    enabled: isAuthenticated && !!organizationId,
  });
  
  useEffect(() => {
    if (features?.inspectorPackageProAllowed) {
      setHasPackagePro(true);
    } else {
      setHasPackagePro(false);
    }
  }, [features?.inspectorPackageProAllowed, setHasPackagePro]);
  
  // Check if we're on checks page or report page
  const isChecksOrReportPage = currentLocation.pathname === '/checks' || currentLocation.pathname.includes('/report/');
  const showPackageProIcon = isAuthenticated && hasPackagePro && isChecksOrReportPage;
  const isContactNotAuthenticatedPage = currentLocation.pathname === '/contact' && !isAuthenticated;
  const isSignUpByInvitePage = currentLocation.pathname === '/signupbyinvite';
  const isSignUpPage = currentLocation.pathname === '/signup';
  const isPricingNotAuthenticatedPage = currentLocation.pathname === '/pricing' && !isAuthenticated;
  const isZoneNamesNotAuthenticatedPage = currentLocation.pathname === '/fix' && !isAuthenticated;
  const isLanguageVisible = isContactNotAuthenticatedPage || isSignUpByInvitePage || isSignUpPage || isPricingNotAuthenticatedPage || isZoneNamesNotAuthenticatedPage;
  const adminRoutes = [

    {
      path: '/admin/business/:businessId/project/:projectId/edit-ticket-dsb/:revisionId',
    },
    {
      path: '/admin/business/:businessId/project/:projectId/revision/:revisionId/:ticketId',
    },
    {
      path: '/admin/dashboard',
    },
    {
      path: '/admin/tickets',
    },
    {
      path: '/admin/add-business',
    },
    {
      path: '/admin/revisions',
    },
    {
      path: '/admin/inspections',
    },
    {
      path: '/admin/organization',
    },
    {
      path: '/admin/transactions',
    },
    {
      path: '/admin/users',
    },
    {
      path: '/admin/projects',
    },
    {
      path: '/admin/stats',
    },


  ];

  const { data: dataOrganization } = useQuery({
    queryKey: ['getOrganizationById', organizations?.id],
    queryFn: () => GetUserOrganization(organizations?.id),
    staleTime: 0,
    refetchOnWindowFocus: true,
    enabled: !!organizations?.id
  })
  const { data: dataVersion } = useQuery({
    queryKey: ['version'],
    queryFn: getVersion,
    staleTime: 60 * 5 * 1000,
    enabled: role === USER_ROLES.ADMIN
  });
  ;

  const { data: maintenanceStatus, isLoading: isLoadingMaintenanceStatus } = useQuery('maintenanceStatus', getMaintenanceStatus, {
    enabled: role === USER_ROLES.ADMIN,
  });

  const [maintenanceMode, setMaintenanceMode] = useState<boolean>(maintenanceStatus?.maintenanceMode ?? false);
  const [openMaintenanceDialog, setOpenMaintenanceDialog] = useState<boolean>(false);

  useEffect(() => {
    if (maintenanceStatus) {
      setMaintenanceMode(maintenanceStatus.maintenanceMode);
    }
  }, [maintenanceStatus]);

  useEffect(() => {
    const matchedRoutes = matchRoutes(adminRoutes, currentLocation.pathname);
    if (matchedRoutes && matchedRoutes.length > 0 && !currentLocation.pathname.endsWith('/result')) {
      setHideBusinessLogo(true);
    }
    else {
      setHideBusinessLogo(false);
    }
  }, [currentLocation.pathname]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    let accPoints = 0;
    if (dataOrganization) {
      accPoints = calcTotalAccountPoints(dataOrganization);
    }
    setAccountPoints(accPoints);
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const logoutHandler = (e: any) => {
    e.preventDefault();
    logout();
  };
  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };



  return (
    <Box className='header-body'>
      <AppBar
        position='static'
        color='transparent'
        style={{
          boxShadow: 'none',
          height: '76px',
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <ButtonIcon
              isActive={Boolean(anchorElNav)}
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
            >
              <MenuIcon />
            </ButtonIcon>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}

              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
            >
              <HeaderMenu isForHeader isRowDirection onClose={handleCloseNavMenu}
              />
            </Menu>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex', flexDirection: "row", alignItems: "flex-end", gap: "10px", justifyContent: "flex-start" } }}>
            <Link to='/'>
              <img src={MainLogo} className='h-8' alt='LOGO' />
            </Link>
            {/* Dashboard button and Package Pro icon */}
            {showPackageProIcon && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                <Tooltip title={t('HEADER_PROJECT_LIST', { defaultValue: 'Go to Dashboard' })}>
                  <ButtonIcon
                    onClick={() => {
                      startTransition(() => {
                        const isInspectorRole = role === USER_ROLES.INSPECTOR || role === USER_ROLES.INSPECTOR_MANAGER;
                        navigate(isInspectorRole ? '/inspections' : '/dashboard');
                      })
                    }}
                    sx={{ color: 'primary.main' }}
                  >
                    <HomeIcon />
                  </ButtonIcon>
                </Tooltip>
                <Tooltip title={verificationSaved ? t('INSPECTION_ADDED_TO_DASHBOARD', { defaultValue: 'Inspection added to dashboard' }) : ''}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {verificationSaved ? (
                      <CheckCircleIcon sx={{ color: '#1bb81e', fontSize: '28px' }} />
                    ) : (
                      <AddIcon sx={{ color: 'primary.main', fontSize: '28px' }} />
                    )}
                  </Box>
                </Tooltip>
              </Box>
            )}
          </Box>
          {role === USER_ROLES.ADMIN &&
            <Box sx={{ ml: 1, mt: 2 }}>
              <Typography fontSize={"11px"} variant='caption' color={"#6b6868"}>{dataVersion?.version}</Typography>
            </Box>

          }
          <Box
            sx={{
              flexGrow: 1,
              gap: "52px",
              display: { xs: "none", md: 'flex', lg: 'flex' },
              mx: "1rem",
              justifyContent: "center",
              fontWeight: 600
            }}
            className='menu-items'
          >
            <HeaderMenu isForHeader />
          </Box>
          {isLoadingMaintenanceStatus ?
            <Box>
              <Skeleton variant="text" width={100} height={38} />
            </Box> : <Box sx={{
              display: { xs: 'none', md: role === USER_ROLES.ADMIN && isAuthenticated ? 'flex' : 'none' }
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={maintenanceMode ?? false}
                    onChange={() => setOpenMaintenanceDialog(true)}
                    color="primary"
                    size="medium"
                  />
                }
                sx={{
                  height: '36px',
                  display: 'flex',
                  borderRadius: '5px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  pr: '10px',
                  backgroundColor: maintenanceMode ? 'error.main' : '#1bb81e',
                }}
                label={t('MAINTENANCE_MODE')}
              />
            </Box>
          }
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: 'flex', md: 'none' }

            }}
          >
            <Link to='/'>
              <img src={MainLogo} style={{ marginTop: "15px" }} className='h-12' alt='LOGO' />
            </Link>
          </Box>
          <Box display={isLanguageVisible ? 'flex' : 'none'}>
            <LanguageSwitcher />
          </Box>

          <Box
            sx={{
              display: !isAuthenticated ? 'flex' : 'none',
              justifyContent: 'space-around',
              gap: 1,
              alignItems: 'center',
              ml: 2
            }}
          >

            <ButtonCustom
              variant='contained'
              size='small'
              color='primary'
              onClick={(e: any) => {
                e.preventDefault();
                navigate('/login');
              }}
            >
              {t('LOGIN_BUTTON')}
            </ButtonCustom>
          </Box>
          <Box
            sx={{
              display: role === USER_ROLES.ADMIN ? 'flex' : 'none',
              ml: 2
            }}
          >
            <ButtonCustom
              size='small'
              sx={{
                width: '80px',
              }}
              variant='contained'
              color='primary'
              className='secondary-btn'
              onClick={(e: any) => {
                adminLogout();
              }}
            >
              {t('HEADER_PROFILE_MENU_LOGOUT')}
            </ButtonCustom>
          </Box>


          <Box
            sx={{
              flexGrow: 0,
              display: isAuthenticated && !hideBusinessLogo ? 'flex' : 'none',
            }}
          >
            <Tooltip title=''>
              <Box onClick={handleOpenUserMenu} sx={{ pl: 2, cursor: "pointer" }}>
                {dataOrganization?.image_url ? (<img
                  alt='User Image'
                  style={{ width: '60px', minHeight: '60px', padding: "6px", objectFit: "contain" }}
                  src={dataOrganization?.image_url}
                />) :
                  (<GroupsIcon
                    sx={{ backgroundColor: "#BDBDBD", color: "white", width: '60px', minHeight: '60px', padding: '10px' }} />)}
              </Box>
            </Tooltip>

            <Menu
              sx={{ mt: '55px', width: "100%" }}
              id='menu-appbar'
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: language === 'he' ? 'left' : 'right',
              }}

              transformOrigin={{
                vertical: 'top',
                horizontal: language === 'he' ? 'left' : 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <Box className='profile-menu-container'>
                <Box className='profile-menu-header'>
                  <Box
                    onClick={() => {
                      if (role === USER_ROLES.ADMIN) {
                        navigate(`/edit/business/${organizations?.id}`);
                      } else {
                        navigate(`/edit/business`);
                      }
                      handleCloseUserMenu();
                    }}
                    sx={{
                      cursor: 'pointer',
                    }}
                  >
                    {dataOrganization?.image_url ? (<img
                      alt='User Image'
                      style={{ width: '85px', minHeight: '85px', objectFit: "contain" }}
                      src={dataOrganization?.image_url}
                    />) :
                      (<GroupsIcon
                        sx={{ backgroundColor: "#BDBDBD", color: "white", width: '85px', minHeight: '85px', padding: '10px' }} />)}
                  </Box>
                  <Box sx={{}} className='profile-menu-header-left'>
                    <Typography variant='subtitle2' component='p'>
                      {user?.name}
                    </Typography>
                    <Typography variant='subtitle2' component='p'>
                      {dataOrganization?.company_name}
                    </Typography>
                    <Box className='points'>
                      {dataOrganization?.hasUnlimited ? (() => {
                        const accumulatedExpiryDate = getAccumulatedExpiryDate(dataOrganization);
                        return accumulatedExpiryDate ? (
                          <>
                            <Typography variant='subtitle2' component='p' className='points'>
                              {t('UNLIMITED')}
                            </Typography>
                            <Typography variant='subtitle2' component='p' className='points'>
                              {moment.utc(accumulatedExpiryDate).local().format('DD/MM/YYYY')}
                            </Typography>
                          </>
                        ) : null;
                      })() : (
                        <Typography variant='subtitle2' component='p' className='points'>
                          {`${accountPoints ?? 0} ${t('BUSINESS_SETTING_TRANSACTION_CREDITS')}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                </Box>
                <Box
                  className='profile-menu-footer'

                >
                  {role !== USER_ROLES.ADMIN ? (
                    <ButtonCustom
                      variant='outlined'
                      size='small'
                      sx={{
                        width: '100px',
                      }}
                      onClick={logoutHandler}
                      className='secondary-btn'
                    >
                      {t('HEADER_PROFILE_MENU_LOGOUT')}
                    </ButtonCustom>
                  ) : (
                    <ButtonCustom
                      size='small'
                      sx={{
                        width: '100px',
                      }}
                      variant='outlined'
                      onClick={() => {
                        navigate(`/admin/business/${organizations?.id}/projects`);
                        handleCloseUserMenu();
                      }}
                      className='secondary-btn'
                    >
                      {t('HEADER_PROJECT_LIST')}
                    </ButtonCustom>
                  )}

                </Box>
              </Box>
            </Menu>
          </Box>
        </Toolbar>
        {/* <Divider sx={{color:"#E7E9F5", borderColor:"#E7E9F5", borderWidth:"1px"}} orientation="horizontal"/>
 */}
      </AppBar>

      <GenericDialog open={openMaintenanceDialog} onClose={() => setOpenMaintenanceDialog(false)} title={t('MAINTENANCE_MODE_DIALOG_TITLE')} cancelButtonText={t('NO')} confirmButtonText={t('YES')} onConfirm={() => {
        toggleMaintenanceMode(!maintenanceMode);
        setMaintenanceMode(!maintenanceMode);
        setOpenMaintenanceDialog(false);
      }}>
        {maintenanceMode ? <Typography>{t('MAINTENANCE_MODE_MESSAGE_ONLINE')}</Typography> : <Typography>{t('MAINTENANCE_MODE_MESSAGE_OFFLINE')}</Typography>}
      </GenericDialog>
    </Box>
  );
};
export default Header;
